"use client";

import { Card, MenuItem, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import { useFormikContext } from "formik";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { DATETIME_FILTER_FORMAT } from "@/helpers/date-helpers";
import {
  IServiceInterfaceLog,
  IServiceInterfaceLogType,
} from "@/interfaces/service-interface.interface";
import { ServiceInterfaceMessages } from "@/messages/service-interface/service-interface.messages";
import { FilterType, IFilter } from "@/types/filter.types";

const GRID_SIZES = {
  LARGE: { lg: 3, md: 4, sm: 6, xs: 12 },
  SMALL: { lg: 2, md: 4, sm: 6, xs: 12 },
} as const;

const DATE_TIME_PICKER_SLOT_PROPS = {
  textField: {
    fullWidth: true,
    InputLabelProps: { shrink: true },
  },
  layout: {
    sx: {
      "& .MuiDivider-root": { margin: 0 },
      backgroundImage: "unset",
    },
  },
  digitalClockSectionItem: {
    style: {
      minWidth: "unset",
    },
  },
} as const;

const LOG_TYPE_ALL = "";

type Props = {
  messages: ServiceInterfaceMessages;
  logTypes: IServiceInterfaceLogType[];
  onClear?: () => void;
};

function FiltersForm({ messages, logTypes, onClear }: Props) {
  const { values, setValues, submitForm } = useFormikContext<IFilter<IServiceInterfaceLog>[]>();

  const getValueFromProperty = (property: string) =>
    values.find((f) => String(f.property) === property)?.value ?? "";

  const upsertFilter = (
    property: "LogType" | "LogMessage" | "RowDate",
    value: string | null,
    filterType: FilterType
  ) => {
    setValues((prev) => {
      const without = prev.filter((f) => String(f.property) !== property);
      if (value === null || value === "") return without;
      return [
        ...without,
        {
          property: property as keyof IServiceInterfaceLog,
          filterType,
          value,
        },
      ];
    });
  };

  const handleRowDateFrom = (value: dayjs.Dayjs | null) => {
    setValues((prev) => {
      const without = prev.filter(
        (f) => String(f.property) !== "RowDate" || f.filterType !== FilterType.GreaterThan
      );
      if (!value) return without;
      return [
        ...without,
        {
          property: "RowDate" as keyof IServiceInterfaceLog,
          value: value.toISOString(),
          filterType: FilterType.GreaterThan,
        },
      ];
    });
  };

  const handleRowDateTo = (value: dayjs.Dayjs | null) => {
    setValues((prev) => {
      const without = prev.filter(
        (f) => String(f.property) !== "RowDate" || f.filterType !== FilterType.LessThan
      );
      if (!value) return without;
      return [
        ...without,
        {
          property: "RowDate" as keyof IServiceInterfaceLog,
          value: value.toISOString(),
          filterType: FilterType.LessThan,
        },
      ];
    });
  };

  const getRowDateFrom = () => {
    const filter = values.find(
      (f) => String(f.property) === "RowDate" && f.filterType === FilterType.GreaterThan
    );
    return filter?.value ? dayjs(String(filter.value)) : null;
  };

  const getRowDateTo = () => {
    const filter = values.find(
      (f) => String(f.property) === "RowDate" && f.filterType === FilterType.LessThan
    );
    return filter?.value ? dayjs(String(filter.value)) : null;
  };

  const handleClearFilters = () => {
    setValues([]);
    onClear?.();
  };

  return (
    <Card>
      <MDBox p={2}>
        <Grid container spacing={2}>
          <Grid size={GRID_SIZES.LARGE}>
            <TextField
              select
              label={messages.logTypeLabel}
              value={String(getValueFromProperty("LogType") || LOG_TYPE_ALL)}
              onChange={(e) => {
                const v = e.target.value;
                upsertFilter("LogType", v === LOG_TYPE_ALL ? null : v, FilterType.Equals);
              }}
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
                select: {
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (!selected) return "\u00a0";
                    const type = logTypes.find((t) => t.code === selected);
                    return (type?.label ?? String(selected)) as React.ReactNode;
                  },
                },
              }}
            >
              {logTypes.map((type) => (
                <MenuItem key={type.code} value={type.code}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={GRID_SIZES.LARGE}>
            <TextField
              label={messages.logMessageLabel}
              value={String(getValueFromProperty("LogMessage"))}
              onChange={(e) => upsertFilter("LogMessage", e.target.value, FilterType.Contains)}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={GRID_SIZES.LARGE}>
            <DateTimePicker
              label={messages.rowDateFromLabel}
              value={getRowDateFrom()}
              onChange={handleRowDateFrom}
              format={DATETIME_FILTER_FORMAT}
              ampm={false}
              timeSteps={{ minutes: 1 }}
              slotProps={DATE_TIME_PICKER_SLOT_PROPS}
            />
          </Grid>
          <Grid size={GRID_SIZES.LARGE}>
            <DateTimePicker
              label={messages.rowDateToLabel}
              value={getRowDateTo()}
              onChange={handleRowDateTo}
              format={DATETIME_FILTER_FORMAT}
              ampm={false}
              timeSteps={{ minutes: 1 }}
              slotProps={DATE_TIME_PICKER_SLOT_PROPS}
            />
          </Grid>
          <Grid size={12}>
            <Grid container spacing={2}>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="light" onClick={handleClearFilters} fullWidth>
                  {messages.clearFilterButton}
                </MDButton>
              </Grid>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="dark" onClick={submitForm} fullWidth>
                  {messages.filterButton}
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default FiltersForm;
